<div class="container h-full flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
  <div class="flex items-center mb-3 min-h-10">
    <h2 class="text-2xl font-medium">Apparaten</h2>
    <button
      *appHasPermission="'device:update'"
      mat-button
      class="sm:ml-auto"
      (click)="handleDeviceAttach()"
      [disabled]="isAddDeviceDisabled()"
    >
      <mat-icon>add</mat-icon>

      {{
        activeCategory() === 'access-point'
          ? 'Access point koppelen'
          : 'Apparaat koppelen'
      }}
    </button>
  </div>

  <div class="mb-3">
    <mat-chip-listbox
      hideSingleSelectionIndicator
      class="inline-flex! py-1 px-2 bg-white rounded-full [--mdc-chip-outline-color:transparent] [--mdc-chip-container-shape-radius:24px] [--mdc-chip-label-text-color:var(--color-gray-500)] [--mdc-chip-elevated-selected-container-color:var(--color-secondary-200)] [--mdc-chip-selected-label-text-color:var(--color-secondary-800)]"
    >
      <mat-chip-option
        value="robot"
        [selected]="activeCategory() === 'robot'"
        [selectable]="activeCategory() !== 'robot'"
        (selectionChange)="$event.selected && activeCategory.set('robot')"
      >
        Robots
      </mat-chip-option>
      <mat-chip-option
        value="access-point"
        [selected]="activeCategory() === 'access-point'"
        [selectable]="activeCategory() !== 'access-point'"
        (selectionChange)="$event.selected && activeCategory.set('access-point')"
      >
        Access points
      </mat-chip-option>
    </mat-chip-listbox>
  </div>

  <div class="bg-white p-4 rounded-t-lg">
    <mat-form-field
      subscriptSizing="dynamic"
      class="w-full [--mat-form-field-container-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
    >
      <mat-icon matPrefix> search </mat-icon>

      <input
        #searchInput
        matInput
        placeholder="Zoeken op ID of naam..."
        [disabled]="isSearchDisabled()"
        [ngModel]="keyword()"
        (ngModelChange)="handleSearch(searchInput.value)"
      />

      @if (keyword()) {
        <button mat-icon-button matSuffix class="mr-1" (click)="clearSearch()">
          <mat-icon>cancel</mat-icon>
        </button>
      }
    </mat-form-field>
  </div>

  <app-data-table-container class="rounded-b-lg">
    <table
      mat-table
      matSort
      matSortDirection="asc"
      matSortActive="label"
      matSortDisableClear
      [matSortDisabled]="hasNoData()"
      (matSortChange)="handleSortChange($event)"
      [dataSource]="dataSource()"
    >
      <ng-container matColumnDef="idAlias" sticky>
        <th mat-header-cell *matHeaderCellDef># Apparaat ID</th>
        <td mat-cell *matCellDef="let element">
          {{ element.idAlias }}
        </td>
      </ng-container>

      <ng-container matColumnDef="label">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Naam</th>
        <td mat-cell *matCellDef="let element">
          <span
            class="w-min max-w-40 truncate block"
            [matTooltip]="element.label"
            matTooltipPosition="after"
            >{{ element.label ?? 'Geen naam' }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let element">
          {{ element.type | mapFrom: robotTypeMap }}
        </td>
      </ng-container>

      <ng-container matColumnDef="serialNumber">
        <th mat-header-cell *matHeaderCellDef>Serienummer</th>
        <td mat-cell *matCellDef="let element">
          {{ element.metadata?.serial_number || '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="batteryLevel">
        <th mat-header-cell *matHeaderCellDef>Batterijniveau</th>
        <td mat-cell *matCellDef="let element">
          @if (element.preset?.telemetry?.battery_level) {
            <div class="flex items-center gap-2">
              @if (element.latestSession?.status === 'open') {
                <span>{{ element.preset.telemetry.battery_level + '%' }}</span>
              } @else {
                <span>0%</span>
              }

              <div class="flex items-center gap-1">
                @if (
                  element.preset.telemetry.is_charging &&
                  element.latestSession?.status === 'open'
                ) {
                  <div
                    class="size-6 p-0.5 rounded-full bg-green-200 text-green-800"
                    matTooltip="Opladen"
                  >
                    <mat-icon class="size-5 text-xl! leading-none" inline>
                      charger
                    </mat-icon>
                  </div>
                }

                @if (element.preset.telemetry.battery_level <= 20) {
                  <div
                    class="size-6 p-0.5 rounded-full bg-orange-200 text-orange-800"
                    matTooltip="Batterij laag"
                  >
                    <mat-icon class="size-5 text-xl! leading-none" inline>
                      error
                    </mat-icon>
                  </div>
                }
              </div>
            </div>
          } @else {
            -
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let element">
          @let status =
            element.latestSession && element.latestSession.status === 'open'
              ? 'online'
              : 'offline';
          <span
            class="text-xs font-medium px-3 py-2 rounded-full"
            [ngClass]="{
              'bg-red-100 text-red-950': status === 'offline',
              'bg-green-100 text-green-950': status === 'online',
            }"
          >
            {{ status | titlecase }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="duration">
        <th mat-header-cell *matHeaderCellDef>Duratie</th>
        <td mat-cell *matCellDef="let element">
          @if (element.latestSession?.createdAt) {
            @if (element.latestSession.status === 'open') {
              <span class="inline-block min-w-30">{{
                currentTime() | formatInterval: element.latestSession.createdAt
              }}</span>
            } @else {
              0d 0h 0m 0s
            }
          } @else {
            Niet beschikbaar
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="unit">
        <th mat-header-cell *matHeaderCellDef>Unit</th>
        <td mat-cell *matCellDef="let element">
          @if (element.unitId === currentUnitId()) {
            <span
              class="w-min max-w-40 truncate block"
              [matTooltip]="unitMap().get(element.unitId) ?? element.unitId"
              matTooltipPosition="after"
            >
              {{ unitMap().get(element.unitId) ?? element.unitId }}
            </span>
          } @else {
            <button
              class="font-medium text-blue-500 hover:underline hover:cursor-pointer w-min max-w-40 truncate block"
              matTooltip="Ga naar deze unit"
              matTooltipPosition="after"
              (click)="activateUnit(element.unitId)"
            >
              {{ unitMap().get(element.unitId) ?? element.unitId }}
            </button>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let element">
          <div>
            <button
              *appHasPermission="'device:update'"
              mat-icon-button
              matTooltip="Naam bewerken"
              (click)="handleEditLabel(element)"
            >
              <mat-icon>edit</mat-icon>
            </button>
            <button
              *appHasPermission="'device:update'"
              mat-icon-button
              matTooltip="Apparaat loskoppelen"
              (click)="handleDeviceDetach(element)"
            >
              <mat-icon>link_off</mat-icon>
            </button>
          </div>
        </td>
      </ng-container>

      <tr
        mat-header-row
        *matHeaderRowDef="displayedColumns(); sticky: true"
        class="whitespace-nowrap"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns()"
        class="hover:!bg-gray-100 whitespace-nowrap"
      ></tr>

      <tr class="mat-mdc-row" *matNoDataRow>
        <td
          class="mat-mdc-cell mdc-data-table__cell !border-none"
          [colSpan]="displayedColumns().length"
        >
          Geen resultaten gevonden
        </td>
      </tr>
    </table>

    <mat-paginator
      class="border-t border-(--mat-table-row-item-outline-color)"
      (page)="handlePageChange($event)"
      [pageSize]="pageSize()"
      [pageSizeOptions]="[25, 50, 100]"
    ></mat-paginator>
  </app-data-table-container>
</div>
